import { useCallback, useEffect, useMemo, useState } from 'react';

import { sendNotification } from '../../UI/Notification';
import { MESSAGES } from '../../constants/FieldNominations';
import { get } from '../../services/GenesisApi';
import {
  GetFieldAuditLogsParams,
  IFieldAuditLogs,
  IFieldType,
  ILogsState,
} from '../../types/FieldNominations.type';

type GetFieldAuditLogsType = ({
  url,
  hasLogs,
  fieldType,
  origin,
}: GetFieldAuditLogsParams) => ILogsState & {
  resetLogsOnCloseModal: () => void;
  onExpandLogs: (id: number) => void;
  onPayloadLogs?: (auditLog: IFieldAuditLogs) => void;
};

const useGetFieldAuditLogs: GetFieldAuditLogsType = ({
  url,
  hasLogs = false,
  fieldType,
}: GetFieldAuditLogsParams) => {
  const initialAuditLogs = {
    hasLoading: false,
    auditLogs: [],
  };
  const [{ hasLoading, auditLogs }, setAuditLogs] =
    useState<ILogsState>(initialAuditLogs);

  const handleResetAuditLogsOnClose = () => {
    setAuditLogs((prevState) => ({
      ...prevState,
      auditLogs: [],
    }));
  };

  const handleExpandCollapseOnClick = useCallback(
    (id: number) => {
      setAuditLogs((prevState) => ({
        ...prevState,
        auditLogs: prevState.auditLogs.map((log) => {
          if (log.id === id) {
            return {
              ...log,
              expanded: !log.expanded,
            };
          }
          return log;
        }),
      }));
    },
    [setAuditLogs]
  );

  const handleSetAuditLogs = (data) => {
    setAuditLogs((prevState) => ({
      ...prevState,
      auditLogs: data.map((log) => {
        return {
          id: log.id,
          note: log.note,
          ...(fieldType === IFieldType.FIELD_NORMS
            ? {
                setaPayload: null,
                createdDateTimeinGMT: log.createdDateTimeinGMT,
                payload: log.payload,
              }
            : {
                setaPayload: log.auditLog,
                createdDateTimeinGMT: log.modifiedDate,
              }),
        };
      }),
    }));
  };

  const getAuditLogs = useCallback(
    (apiUrl: string) => {
      setAuditLogs((prevState) => ({
        ...prevState,
        hasLoading: true,
      }));
      get(apiUrl)
        .then((response) => {
          const { data = [] } = response;
          handleSetAuditLogs(data);
        })
        .catch(() => {
          sendNotification({
            type: 'error',
            message: MESSAGES.SERVER_ERROR,
          });
        })
        .finally(() => {
          setAuditLogs((prevState) => ({
            ...prevState,
            hasLoading: false,
          }));
        });
    },
    [fieldType]
  );

  const handleOnOpenPayload = (auditLog: IFieldAuditLogs) => {
    if (auditLog.setaPayload && !auditLog.expanded) {
      handleExpandCollapseOnClick(auditLog.id);
    }
    if (auditLog.setaPayload) {
      return;
    }
    setAuditLogs((prevState) => ({
      ...prevState,
      auditLogs: prevState.auditLogs.map((log) => {
        if (log.id === auditLog.id) {
          return {
            ...log,
            setaPayloadLoading: true,
            expanded: true,
          };
        }
        return log;
      }),
    }));
  };

  const sortedAuditLogsWithLatestLogs = useMemo(() => {
    return auditLogs.sort((logA, logB) => logB.id - logA.id);
  }, [auditLogs]);

  useEffect(() => {
    if (hasLogs) {
      getAuditLogs(url);
    }
  }, [hasLogs, url, getAuditLogs]);

  return {
    hasLoading,
    auditLogs: sortedAuditLogsWithLatestLogs,
    resetLogsOnCloseModal: handleResetAuditLogsOnClose,
    onExpandLogs: handleExpandCollapseOnClick,
    onPayloadLogs: handleOnOpenPayload,
  };
};
export default useGetFieldAuditLogs;
